import Mock from 'mockjs'

// 定义服务状态接口
export interface ServiceStatus {
  id: string
  ip: string
  port: number
  status: string
  updateTime: string
  joinTime: string
}

// 生成随机IP地址
const randomIP = function() {
  return `192.168.${Mock.Random.integer(1, 255)}.${Mock.Random.integer(1, 255)}`
}

// 生成随机端口
const randomPort = function() {
  return Mock.Random.integer(1000, 9999)
}

// 生成随机状态
const randomStatus = function() {
  const rand = Mock.Random.integer(0, 999);
  if (rand < 900) {
    return '运行中'; // 90% 概率
  } else if (rand < 901) {
    return '异常'; // 0.1% 概率
  } else {
    return '已停止'; // 9.9% 概率
  }
};

// 生成更新时间：当前时间前7-30天，时间在10:00:00 - 17:30:00之间
const generateUpdateTime = function() {
  const now = new Date(); // 获取当前真实时间
  const daysToSubtract = Mock.Random.integer(7, 30);
  // 创建一个新的Date对象进行日期计算，避免修改原始的now对象
  const date = new Date(now.getTime() - daysToSubtract * 24 * 60 * 60 * 1000);

  const hour = Mock.Random.integer(10, 17);
  const minute = hour === 17 ? Mock.Random.integer(0, 30) : Mock.Random.integer(0, 59);
  const second = Mock.Random.integer(0, 59);

  date.setHours(hour, minute, second);
  // 直接格式化日期对象
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const h = date.getHours().toString().padStart(2, '0');
  const m = date.getMinutes().toString().padStart(2, '0');
  const s = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day} ${h}:${m}:${s}`;
};

// 生成首次加入时间：2023年9月之后，且在更新时间前至少30天
const generateJoinTime = function(updateTime: string) {
  const updateDate = new Date(updateTime);
  
  const minJoinDateLimit = new Date(updateDate.getTime()); 
  minJoinDateLimit.setDate(minJoinDateLimit.getDate() - 30); // 更新时间前30天

  const earliestPossibleJoinDate = new Date('2023-09-01T00:00:00');

  let validMinJoinTimestamp = Math.max(earliestPossibleJoinDate.getTime(), minJoinDateLimit.getTime());
  
  // 确保 validMinJoinTimestamp 确实在 updateDate 之前，并且至少早30天
  if (validMinJoinTimestamp >= updateDate.getTime() - 30 * 24 * 60 * 60 * 1000) {
    // 如果 validMinJoinTimestamp 太接近 updateDate，则强制将其设置为 updateDate 前31天
    // 同时确保不早于 earliestPossibleJoinDate
    validMinJoinTimestamp = Math.max(earliestPossibleJoinDate.getTime(), updateDate.getTime() - 31 * 24 * 60 * 60 * 1000);
  }

  const maxJoinTimestamp = updateDate.getTime() - 24 * 60 * 60 * 1000; // 确保joinTime早于updateTime至少一天

  // 如果计算后的 validMinJoinTimestamp 仍然大于或等于 maxJoinTimestamp，
  // 这意味着无法找到一个满足所有条件的时间点（例如 updateTime 太接近2023-09-01）。
  // 在这种情况下，我们将 joinTime 设置为 earliestPossibleJoinDate，或者 updateTime 前的某一天，以较晚者为准，并确保它早于 updateTime。
  if (validMinJoinTimestamp >= maxJoinTimestamp) {
      // 尝试将 joinTime 设置为 updateTime 的前一天，但不能早于 earliestPossibleJoinDate
      const oneDayBeforeUpdate = updateDate.getTime() - 24 * 60 * 60 * 1000;
      validMinJoinTimestamp = Math.max(earliestPossibleJoinDate.getTime(), oneDayBeforeUpdate - 24*60*60*1000); // 再往前推一天，确保有范围
      if (validMinJoinTimestamp >= oneDayBeforeUpdate) { // 如果还是不行，就取最早可能日期
          validMinJoinTimestamp = earliestPossibleJoinDate.getTime();
      }
  }
  
  const randomTimestamp = Mock.Random.integer(validMinJoinTimestamp, maxJoinTimestamp);
  const randomJoinDate = new Date(randomTimestamp);

  const year = randomJoinDate.getFullYear();
  const month = (randomJoinDate.getMonth() + 1).toString().padStart(2, '0');
  const day = randomJoinDate.getDate().toString().padStart(2, '0');
  const hour = randomJoinDate.getHours().toString().padStart(2, '0');
  const minute = randomJoinDate.getMinutes().toString().padStart(2, '0');
  const second = randomJoinDate.getSeconds().toString().padStart(2, '0');
  
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

// 生成服务状态数据的模板
const serviceStatusTemplate = () => {
  const updateTime = generateUpdateTime();
  const joinTime = generateJoinTime(updateTime);
  return {
    'id|+1': 1,
    ip: randomIP(),
    port: randomPort(),
    status: randomStatus(),
    updateTime: updateTime,
    joinTime: joinTime,
  };
};

// 为每个协议生成1-5条随机服务状态数据
export const generateServiceStatus = function(count = 3) {
  const templates = [];
  for (let i = 0; i < count; i++) {
    templates.push(serviceStatusTemplate());
  }
  return Mock.mock({ data: templates }).data;
};

// 生成所有协议的服务状态数据 (如果需要)
export const generateAllProtocolServiceStatus = function(protocols: string[]) {
  const result: Record<string, ServiceStatus[]> = {}
  
  protocols.forEach(protocol => {
    const count = Mock.Random.integer(1, 5)
    result[protocol] = generateServiceStatus(count)
  })
  
  return result
}